package com.bikompanion.ui.composables

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.CubicBezierEasing
import androidx.compose.animation.core.VisibilityThreshold
import androidx.compose.animation.core.tween
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutHorizontally
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectVerticalDragGestures
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.displayCutout
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.union
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.Button
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.backhandler.PredictiveBackHandler
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.GraphicsLayerScope
import androidx.compose.ui.graphics.TransformOrigin
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.nestedscroll.NestedScrollConnection
import androidx.compose.ui.input.nestedscroll.NestedScrollSource
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.Velocity
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.lerp
import androidx.compose.ui.zIndex
import dev.chrisbanes.haze.HazeInput
import dev.chrisbanes.haze.HazeState
import dev.chrisbanes.haze.glass.ChromaticAberrationMode
import dev.chrisbanes.haze.glass.GlassOptics
import dev.chrisbanes.haze.glass.GlassStyle
import dev.chrisbanes.haze.glass.OpticalSizeValue
import dev.chrisbanes.haze.glass.hazeGlass
import kotlinx.coroutines.launch
import com.bikompanion.ui.preview.GlassPreviewSurface
import com.bikompanion.ui.preview.PreviewLightDarkCompact
import com.bikompanion.ui.preview.PreviewLightDarkExpanded
import com.bikompanion.ui.strings.LocalStrings
import kotlin.coroutines.cancellation.CancellationException
import kotlin.math.min
import kotlin.math.roundToInt

/** Matches [androidx.compose.material3.BottomSheetDefaults.SheetMaxWidth]. */
private val SheetMaxWidth = 640.dp

/** Width of the end-anchored panel the sheet becomes when `docked`. */
internal val DockedSheetWidth = 380.dp

private val DockedShape = RoundedCornerShape(28.dp)

/**
 * Opacity of the `surfaceContainer` plate over the glass. The sheet refracts the nav bar, the FABs
 * and their labels as well as the map, so without it light text behind the sheet competes with the
 * light text on it in dark theme. Done here rather than in [defaultGlassStyle] because a plate
 * changes in the same frame as the text; a theme-dependent glass style flashes on a theme change.
 */
private const val SheetPlateAlpha = 0.6f

/** The 16dp margin on either side of a docked panel, which it must clear to leave the screen. */
private val DockedMargins = 32.dp

/** Matches [androidx.compose.material3.BottomSheetDefaults.ExpandedShape] (`MaterialTheme.shapes.extraLarge`). */
private val SheetShape = RoundedCornerShape(topStart = 28.dp, topEnd = 28.dp)

/** Matches Material3's `PredictiveBackMaxScaleXDistance`/`YDistance` for bottom sheets. */
private val PredictiveBackMaxScaleXDistance = 48.dp
private val PredictiveBackMaxScaleYDistance = 24.dp

// The check-bike action is layered at 2f so it can overlap the navigation bar. Sheets are modal
// content and must remain above that action as well as every other map overlay.
private const val BottomSheetZIndex = 3f

/** Material's predictive-back curve — most of the travel lands early in the gesture. */
private val BackEasing = CubicBezierEasing(0.1f, 0.1f, 0f, 1f)

/**
 * Deliberately not `@Composable`, and callers `remember` it themselves: Compose Hot Reload marks the
 * composable that references edited code as dirty, and a value-returning composable has no restart
 * scope of its own, so tweaking these values used to reset the whole app (map included). As a plain
 * function, the dirty scopes are its (restartable) callers, which recompose in place.
 *
 * [tint] is the raw `primaryContainer`; the alpha is applied here so it can be tuned with the rest.
 *
 * [shape] must be the shape the surface is clipped to: the glass lights its rim and bends light
 * along this outline, so a mismatch (e.g. the sheet's top-only rounding on a capsule) draws
 * highlights along edges that aren't there.
 *
 * Nothing here depends on the theme except [tint]. A `whitePoint` that flipped with it left a bright
 * or dark band round the glass edge for a few frames on every theme change: Haze re-records the
 * refraction layer it caches a beat after the Compose colours have already switched, and the
 * white point is what that layer's edge is brightened or darkened by. Contrast against the map is
 * left to the plates the callers lay over the glass, which do change in the same frame.
 */
fun defaultGlassStyle(
    tint: Color,
    shape: RoundedCornerShape = SheetShape,
): GlassStyle =
    GlassStyle.clear.then {
        tint(tint.copy(alpha = 0.1f))
        optics(
            GlassOptics(
                refractionHeightFraction = 0.45f,
                refractionDisplacement = 80.dp,
                blurRadius = OpticalSizeValue.Fixed(3.dp),
                refractionFoldStrength = 1f,
            ),
        )
        chromaticAberrationStrength(1f)
        shape(shape)
        chromaticAberrationMode(ChromaticAberrationMode.Full)
        ambientResponse(0f)
        whitePoint(0f)
    }

/**
 * The app's bottom sheet: a glass surface that blurs whatever [hazeState] is capturing behind it.
 *
 * `ModalBottomSheet` opens its own Dialog surface, which Haze can't blur across — so this sheet is
 * drawn as plain content sharing the map's render surface instead, which means it also owns the
 * pieces Material would otherwise give us for free: the enter/exit transition, drag-to-dismiss from
 * both the handle and the content's own scroll, and the predictive-back treatment.
 *
 * It always opens fully expanded — there is no half-expanded stop to drag through.
 *
 * When [docked] (wide windows) it is an end-anchored side panel instead: it slides in from the
 * side, has a close button in place of the drag handle, and drops the full-screen click-catcher so
 * the map stays pannable and tapping another marker swaps the panel's content in place.
 *
 * Callers that draw from state cleared on dismissal need to hold the last-known values themselves,
 * otherwise the content empties out before the exit transition has played.
 *
 * [footer] is pinned below the scrolling [content] and never scrolls with it — for the one action
 * that has to stay within reach however long the content gets.
 */
@Composable
internal fun GlassBottomSheet(
    visible: Boolean,
    hazeState: HazeState,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
    docked: Boolean = false,
    dockedWidth: Dp = DockedSheetWidth,
    verticalArrangement: Arrangement.Vertical = Arrangement.spacedBy(4.dp),
    footer: (@Composable ColumnScope.() -> Unit)? = null,
    content: @Composable ColumnScope.() -> Unit,
) {
    // A docked panel travels its own width plus margins, so it comes from the edge rather than
    // popping in a quarter of the way along, and covers the same distance the controls beside it
    // step aside by.
    val dockedTravelPx = with(LocalDensity.current) { (dockedWidth + DockedMargins).roundToPx() }
    // The footer goes null the instant the content stops needing one, so the last non-null one is
    // kept for its exit animation to still have something to draw.
    var heldFooter by remember { mutableStateOf(footer) }
    if (footer != null) {
        heldFooter = footer
    }
    AnimatedVisibility(
        visible = visible,
        enter = fadeIn(tween(durationMillis = 180)) + if (docked) {
            slideInHorizontally(
                animationSpec = dockedPanelSpring(IntOffset.VisibilityThreshold),
                initialOffsetX = { dockedTravelPx },
            )
        } else {
            slideInVertically(initialOffsetY = { it / 4 })
        },
        exit = fadeOut(tween(durationMillis = 180)) + if (docked) {
            slideOutHorizontally(
                animationSpec = dockedPanelSpring(IntOffset.VisibilityThreshold),
                targetOffsetX = { dockedTravelPx },
            )
        } else {
            slideOutVertically(targetOffsetY = { it / 4 })
        },
        modifier = modifier.zIndex(BottomSheetZIndex),
    ) {
        // Hoisted above the Surface (which the offset moves), the drag handle, and the
        // scrollable content's nested-scroll connection below, so all three drive the same drag.
        var offsetY by remember { mutableFloatStateOf(0f) }
        var heightPx by remember { mutableFloatStateOf(0f) }
        val dragScope = rememberCoroutineScope()
        // Dismisses past a third of the sheet's own height, otherwise springs back to 0 —
        // shared by the handle's pointerInput and the content's nested-scroll fling below.
        val finishDrag: suspend () -> Unit = {
            if (heightPx > 0f && offsetY > heightPx / 3f) {
                onDismiss()
            } else {
                Animatable(offsetY).animateTo(0f) { offsetY = value }
            }
        }
        // Material3's own predictive-back treatment for bottom sheets: the sheet shrinks toward
        // the bottom of the screen as the gesture progresses, and springs back if it's cancelled.
        // Deliberately not reset on dismiss — the shrunk sheet is what slides away on exit.
        val backProgress = remember { Animatable(0f) }
        SheetPredictiveBack(
            enabled = visible,
            onProgress = { backProgress.snapTo(it) },
            onCancel = { backProgress.animateTo(0f) },
            onDismiss = onDismiss,
        )
        // Mirrors Material3's own bottom sheet: a NestedScrollConnection on the scrollable
        // content lets a downward drag fall through to the sheet once the content can't scroll
        // up any further (already at its top), and an upward drag retracts the sheet before the
        // content starts scrolling again.
        val sheetNestedScrollConnection = remember {
            object : NestedScrollConnection {
                override fun onPreScroll(
                    available: Offset,
                    source: NestedScrollSource,
                ): Offset {
                    if (source != NestedScrollSource.UserInput || offsetY <= 0f || available.y >= 0f) {
                        return Offset.Zero
                    }
                    val consumed = -minOf(offsetY, -available.y)
                    offsetY += consumed
                    return Offset(0f, consumed)
                }

                override fun onPostScroll(
                    consumed: Offset,
                    available: Offset,
                    source: NestedScrollSource,
                ): Offset {
                    if (source != NestedScrollSource.UserInput || available.y <= 0f) {
                        return Offset.Zero
                    }
                    offsetY += available.y
                    return Offset(0f, available.y)
                }

                override suspend fun onPreFling(available: Velocity): Velocity {
                    if (offsetY <= 0f) return Velocity.Zero
                    finishDrag()
                    return available
                }
            }
        }
        BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
            // Caps the sheet so it never grows tall enough to draw behind the status bar or a
            // camera cutout, without padding short sheets down by that amount when they don't need it.
            val maxSheetHeight = maxHeight - WindowInsets.safeDrawing
                .only(WindowInsetsSides.Top)
                .asPaddingValues()
                .calculateTopPadding()
            // No scrim: catches outside clicks to dismiss without dimming the map, which
            // would otherwise be double-darkened on top of the blur.
            if (!docked) {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .clickable(
                            indication = null,
                            interactionSource = remember { MutableInteractionSource() },
                            onClick = onDismiss,
                        ),
                )
            }
            val glassTint = MaterialTheme.colorScheme.primaryContainer
            val shape = if (docked) DockedShape else SheetShape
            val glassStyle = remember(glassTint, shape) { defaultGlassStyle(glassTint, shape) }
            Surface(
                shape = shape,
                color = Color.Transparent,
                contentColor = MaterialTheme.colorScheme.onPrimaryContainer,
                modifier = (
                    if (docked) {
                        // Insets and margin go outside so the glass stops short of the system bars
                        // and the panel wraps its content up to the space that is left.
                        Modifier
                            .align(Alignment.BottomEnd)
                            .windowInsetsPadding(safeDrawingInsets())
                            .padding(16.dp)
                            .width(dockedWidth)
                            .sheetPredictiveBackScaling(
                                pivotFractionX = 1f,
                                pivotFractionY = 0.5f,
                                uniform = true,
                            ) { backProgress.value }
                    } else {
                        Modifier
                            .align(Alignment.BottomCenter)
                            // A landscape cutout on the side would otherwise sit over the glass.
                            .windowInsetsPadding(
                                WindowInsets.displayCutout
                                    .union(LocalHiddenCutoutInsets.current)
                                    .only(WindowInsetsSides.Horizontal),
                            ).widthIn(max = SheetMaxWidth)
                            .fillMaxWidth()
                            .heightIn(max = maxSheetHeight)
                            .onSizeChanged { heightPx = it.height.toFloat() }
                            .offset { IntOffset(0, offsetY.roundToInt()) }
                            .sheetPredictiveBackScaling { backProgress.value }
                    }
                    // Deliberately no `animateContentSize` here: it animates the surface on a spring that
                    // lags behind content which is already animating its own height (e.g. a collapsing
                    // section), so the content was clipped on expand and left a blank gap on collapse.
                    // Sections animate their own size; the sheet just follows its content, anchored to
                    // its bottom edge.
                ).clip(shape)
                    .hazeGlass(
                        input = HazeInput.Sources(hazeState),
                        style = glassStyle,
                    ).background(MaterialTheme.colorScheme.surfaceContainer.copy(alpha = glassPlateAlpha(SheetPlateAlpha))),
            ) {
                Column(
                    // The docked panel scales uniformly, so its content rides along with it.
                    // The bottom sheet's nav-bar/IME insets sit on this column rather than on the
                    // scroll area or the footer, so the footer can animate in and out without the
                    // padding below it jumping between the two.
                    modifier = if (docked) {
                        Modifier
                    } else {
                        Modifier
                            .contentPredictiveBackScaling { backProgress.value }
                            .navigationBarsPadding()
                            .imePadding()
                    },
                ) {
                    if (docked) {
                        // A side panel has no vertical drag, so dismissal needs a visible control.
                        Row(
                            modifier = Modifier.fillMaxWidth().padding(horizontal = 4.dp),
                            horizontalArrangement = Arrangement.End,
                        ) {
                            IconButton(onClick = onDismiss) {
                                Icon(Icons.Default.Close, contentDescription = LocalStrings.current.actionClose)
                            }
                        }
                    } else {
                        // Kept outside the scrollable content below so dragging it doesn't compete
                        // with that content's own scroll gesture for the touch (the loser on Android).
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .pointerInput(Unit) {
                                    detectVerticalDragGestures(
                                        onVerticalDrag = { change, dragAmount ->
                                            change.consume()
                                            offsetY = (offsetY + dragAmount).coerceAtLeast(0f)
                                        },
                                        onDragEnd = { dragScope.launch { finishDrag() } },
                                    )
                                },
                        ) {
                            SheetDragHandle(modifier = Modifier.align(Alignment.Center))
                        }
                    }
                    val scrollState = rememberScrollState()
                    Column(
                        modifier = Modifier
                            // Shrinks under the sheet's max height so a footer below it stays on screen.
                            .weight(1f, fill = false)
                            .then(if (docked) Modifier else Modifier.nestedScroll(sheetNestedScrollConnection))
                            .scrollEdgeFade(scrollState)
                            .verticalScroll(scrollState)
                            // The docked panel has no nav bar inset under its content, so it needs its own
                            // breathing room above the 28dp rounded corners; 4dp looked cramped there.
                            .padding(
                                start = 16.dp,
                                end = 16.dp,
                                top = 4.dp,
                                bottom = if (docked) 20.dp else 4.dp,
                            ),
                        verticalArrangement = verticalArrangement,
                        content = content,
                    )
                    AnimatedVisibility(
                        visible = footer != null,
                        enter = expandVertically() + fadeIn(),
                        exit = shrinkVertically() + fadeOut(),
                    ) {
                        Column(
                            modifier = Modifier.padding(
                                start = 16.dp,
                                end = 16.dp,
                                top = 8.dp,
                                bottom = if (docked) 20.dp else 12.dp,
                            ),
                            content = { heldFooter?.invoke(this) },
                        )
                    }
                }
            }
        }
    }
}

/**
 * Reports the system back gesture as an eased 0..1 [onProgress] while it's in flight, then either
 * [onDismiss] once it commits or [onCancel] if the user backs out of it.
 *
 * Wraps the deprecated [PredictiveBackHandler] rather than `NavigationEventHandler`, because the
 * latter needs a `NavigationEventDispatcherOwner` in the composition and nothing provides one on
 * desktop or web yet — swap it out once the app grows a navigation host.
 */
@Suppress("DEPRECATION")
@OptIn(ExperimentalComposeUiApi::class)
@Composable
internal fun SheetPredictiveBack(
    enabled: Boolean,
    onProgress: suspend (Float) -> Unit,
    onCancel: suspend () -> Unit,
    onDismiss: () -> Unit,
) {
    PredictiveBackHandler(enabled = enabled) { progress ->
        try {
            progress.collect { onProgress(BackEasing.transform(it.progress)) }
            onDismiss()
        } catch (_: CancellationException) {
            onCancel()
        }
    }
}

/**
 * Shrinks the sheet toward the edge it is anchored to, a copy of Material3's
 * `Modifier.sheetPredictiveBackScaling`. The pivot pins that edge: the bottom for the bottom sheet
 * (the default [pivotFractionY] of 1), the vertical middle of the right edge for the docked panel.
 * It takes at most 48dp off the width and 24dp off the height, which is what keeps the effect
 * subtle on a tall sheet and proportional on a short one.
 *
 * With [uniform] the height follows the width's scale instead. A tall docked panel would otherwise
 * barely shrink vertically (24dp of a few hundred) while looking squashed against its content.
 *
 * [progress] is read inside the layer block so the gesture only re-runs the draw phase.
 */
private fun Modifier.sheetPredictiveBackScaling(
    pivotFractionX: Float = 0.5f,
    pivotFractionY: Float = 1f,
    uniform: Boolean = false,
    progress: () -> Float,
) = graphicsLayer {
    val backProgress = progress()
    scaleX = sheetScaleX(backProgress)
    scaleY = if (uniform) scaleX else sheetScaleY(backProgress)
    transformOrigin = TransformOrigin(pivotFractionX = pivotFractionX, pivotFractionY = pivotFractionY)
}

/**
 * Counter-scales the sheet's content against [sheetPredictiveBackScaling] so it keeps its aspect
 * ratio while the surface around it squashes — Material3's `Modifier.contentPredictiveBackScaling`.
 */
private fun Modifier.contentPredictiveBackScaling(progress: () -> Float) =
    graphicsLayer {
        val backProgress = progress()
        val sheetScaleY = sheetScaleY(backProgress)
        scaleY = if (sheetScaleY != 0f) sheetScaleX(backProgress) / sheetScaleY else 1f
        transformOrigin = TransformOrigin(pivotFractionX = 0.5f, pivotFractionY = 0f)
    }

private fun GraphicsLayerScope.sheetScaleX(progress: Float): Float {
    val width = size.width
    if (width.isNaN() || width == 0f) return 1f
    return 1f - lerp(0f, min(PredictiveBackMaxScaleXDistance.toPx(), width), progress) / width
}

private fun GraphicsLayerScope.sheetScaleY(progress: Float): Float {
    val height = size.height
    if (height.isNaN() || height == 0f) return 1f
    return 1f - lerp(0f, min(PredictiveBackMaxScaleYDistance.toPx(), height), progress) / height
}

@PreviewLightDarkCompact
@Composable
private fun GlassBottomSheetCompactPreview() {
    GlassBottomSheetPreview()
}

@PreviewLightDarkCompact
@Composable
private fun GlassBottomSheetFooterPreview() {
    GlassBottomSheetPreview(hasFooter = true)
}

/** Taller than the window, so the content scrolls under the fade and the footer stays pinned. */
@PreviewLightDarkCompact
@Composable
private fun GlassBottomSheetScrollingPreview() {
    GlassBottomSheetPreview(hasFooter = true, lines = 60)
}

@PreviewLightDarkExpanded
@Composable
private fun GlassBottomSheetDockedPreview() {
    GlassBottomSheetPreview(docked = true)
}

@PreviewLightDarkExpanded
@Composable
private fun GlassBottomSheetDockedFooterPreview() {
    GlassBottomSheetPreview(docked = true, hasFooter = true)
}

@Composable
private fun GlassBottomSheetPreview(
    docked: Boolean = false,
    hasFooter: Boolean = false,
    lines: Int = 3,
) {
    GlassPreviewSurface { hazeState ->
        GlassBottomSheet(
            visible = true,
            hazeState = hazeState,
            onDismiss = {},
            docked = docked,
            footer = if (hasFooter) {
                { Button(onClick = {}, modifier = Modifier.fillMaxWidth()) { Text("Footer action") } }
            } else {
                null
            },
        ) {
            repeat(lines) { Text("Sheet line ${it + 1}") }
        }
    }
}
